import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { trackEvent } from '../../utils/analytics';
import { holdPhotoFor } from './climb-editor/pending-photo';
import { climbPhotoEditorPath } from './missing-photo';
import type { Climb } from '../../types/climb';

/**
 * Giving a climb the photograph it was put up without: what becomes of the file,
 * and where somebody who is not signed in goes instead.
 *
 * The picture is **handed to the editor** rather than uploaded from wherever it
 * was picked (see pending-photo). A photograph of a wall says nothing on its own
 * — forty holds and no telling which three are the climb — so the picture and the
 * circles on it are one act, and the editor is where that act is finished.
 *
 * Two places ask for this and must behave the same way: the camera standing in
 * for a missing thumbnail on a list row, and the one in the picture's place on
 * the climb's own page.
 */
export function useAddClimbPhoto(climb: Climb) {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Worked out when it is asked for rather than up front: every row of a wall
  // runs this hook, and a climb the API sent without a location has no page of
  // its own at all — let alone an editor — so there is nothing to build (see
  // climbUrl, and `wantsPhoto`, which is what decides whether to offer this).
  const editorPath = () => climbPhotoEditorPath(climb);

  return {
    /**
     * Whether there is anybody to take the photo from. The upload carries a
     * token, so a visitor is not shown a camera whose picture would be dropped at
     * the door — they are sent to sign in first.
     */
    signedIn: !!user,
    /** Sign in, and land on the editor they were headed for. */
    signIn: () => navigate('/sign-in', { state: { from: editorPath() } }),
    /** Off to the editor, holding the picture that was just chosen. */
    take: (file: File) => {
      holdPhotoFor(climb.slug, file);
      trackEvent('climb_photo_add_started', { climb_slug: climb.slug });
      navigate(editorPath());
    },
  };
}
